/**
 * Debug helper utilities for troubleshooting white screen issues
 * Available in browser console as window.FarmSmarterDebug
 */

const DebugHelper = {
  /**
   * Get all stored errors from localStorage
   */
  getErrors: function() {
    const errors = {
      lastError: null,
      lastGlobalError: null,
      lastPromiseRejection: null,
      lastChunkError: null,
    };

    try {
      const lastError = localStorage.getItem('lastError');
      if (lastError) errors.lastError = JSON.parse(lastError);
    } catch (e) {
      console.error('Failed to parse lastError:', e);
    }

    try {
      const lastGlobalError = localStorage.getItem('lastGlobalError');
      if (lastGlobalError) errors.lastGlobalError = JSON.parse(lastGlobalError);
    } catch (e) {
      console.error('Failed to parse lastGlobalError:', e);
    }

    try {
      const lastPromiseRejection = localStorage.getItem('lastPromiseRejection');
      if (lastPromiseRejection) errors.lastPromiseRejection = JSON.parse(lastPromiseRejection);
    } catch (e) {
      console.error('Failed to parse lastPromiseRejection:', e);
    }

    try {
      const lastChunkError = localStorage.getItem('lastChunkError');
      if (lastChunkError) errors.lastChunkError = JSON.parse(lastChunkError);
    } catch (e) {
      console.error('Failed to parse lastChunkError:', e);
    }

    console.log('=== STORED ERRORS ===');
    console.log(errors);
    console.log('====================');
    return errors;
  },

  /**
   * Clear all error logs
   */
  clearErrors: function() {
    localStorage.removeItem('lastError');
    localStorage.removeItem('lastGlobalError');
    localStorage.removeItem('lastPromiseRejection');
    localStorage.removeItem('lastChunkError');
    console.log('All error logs cleared');
  },

  /**
   * Clear all caches
   */
  clearCaches: async function() {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
      console.log('All caches cleared:', names);
    } else {
      console.log('Cache API not available');
    }
  },

  /**
   * Clear everything and reload
   */
  hardReset: async function() {
    console.log('Performing hard reset...');
    
    // Clear localStorage
    localStorage.clear();
    console.log('localStorage cleared');
    
    // Clear sessionStorage
    sessionStorage.clear();
    console.log('sessionStorage cleared');
    
    // Clear caches
    await this.clearCaches();
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
      console.log('Service workers unregistered');
    }
    
    console.log('Hard reset complete. Reloading...');
    window.location.reload(true);
  },

  /**
   * Get app info
   */
  getAppInfo: function() {
    const info = {
      userAgent: navigator.userAgent,
      language: localStorage.getItem('language'),
      url: window.location.href,
      timestamp: new Date().toISOString(),
      localStorageSize: new Blob(Object.values(localStorage)).size,
      cacheVersion: localStorage.getItem('cacheVersion'),
    };
    
    console.log('=== APP INFO ===');
    console.log(info);
    console.log('================');
    return info;
  },

  /**
   * Test if app is working
   */
  healthCheck: function() {
    const checks = {
      react: typeof React !== 'undefined',
      reactDOM: typeof ReactDOM !== 'undefined',
      localStorage: typeof localStorage !== 'undefined',
      sessionStorage: typeof sessionStorage !== 'undefined',
      fetch: typeof fetch !== 'undefined',
      caches: 'caches' in window,
      serviceWorker: 'serviceWorker' in navigator,
    };
    
    console.log('=== HEALTH CHECK ===');
    console.log(checks);
    console.log('===================');
    
    const allPassed = Object.values(checks).every(v => v === true);
    if (allPassed) {
      console.log('✅ All health checks passed');
    } else {
      console.warn('⚠️ Some health checks failed');
    }
    
    return checks;
  },

  /**
   * Show help
   */
  help: function() {
    console.log(`
=== FarmSmarter Debug Helper ===

Available commands:
  FarmSmarterDebug.getErrors()      - View all stored errors
  FarmSmarterDebug.clearErrors()    - Clear error logs
  FarmSmarterDebug.clearCaches()    - Clear all caches
  FarmSmarterDebug.hardReset()      - Clear everything and reload
  FarmSmarterDebug.getAppInfo()     - Get app information
  FarmSmarterDebug.healthCheck()    - Run health checks
  FarmSmarterDebug.help()           - Show this help

Example usage:
  > FarmSmarterDebug.getErrors()
  > FarmSmarterDebug.hardReset()

================================
    `);
  }
};

// Make it available globally
if (typeof window !== 'undefined') {
  window.FarmSmarterDebug = DebugHelper;
  
  // Show help on first load in development
  if (process.env.NODE_ENV === 'development') {
    console.log('%c🛠️ FarmSmarter Debug Helper Available', 'color: #14504B; font-size: 14px; font-weight: bold;');
    console.log('%cType FarmSmarterDebug.help() for available commands', 'color: #666; font-size: 12px;');
  }
}

export default DebugHelper;

